<template>
  <hb-basic-page
    :loading="false"
    :title="$tc('quotations.title', 2)"
    :fill-height="false"
    :extension-height="60"
    :search-title="`${$t('quotations.search')}...`"
    :initial-search="listState.q"
    :no-container-padding="$vuetify.breakpoint.xsOnly"
    @handle-search="debounceSearch"
    fluid
    class="quotations-view"
    :no-gutters="true"
  >
    <template v-if="$vuetify.breakpoint.smAndUp" v-slot:app-bar-extension>
      <v-btn class="mb-3 ml-n1" @click="createNew" color="primary">
        <v-icon class="no-gradient" left>mdi-plus</v-icon>
        <span>{{ $t('quotations.new') }}</span>
      </v-btn>
    </template>

    <v-col cols="12" class="gradient-icons">
      <v-btn v-if="$vuetify.breakpoint.xsOnly" @click="createNew" color="primary" :title="$t('quotations.new')" fab bottom right fixed>
        <v-icon class="no-gradient ma-0" left>mdi-plus</v-icon>
      </v-btn>

      <div :class="$vuetify.breakpoint.xsOnly ? 'px-2' : 'px-3'">
        <!-- Näkymän valinta (kuten työmaiden Lista | Aikajana) ja molempia koskeva suodatin: pysyvät paikallaan -->
        <v-row class="pt-3" dense align="center">
          <v-col cols="auto" class="view-toggle">
            <v-btn-toggle :value="datesView ? 'dates' : 'list'" mandatory dense class="elevation-3" @change="v => updateQuery({ view: v === 'dates' ? 'dates' : '' })">
              <v-btn value="list" height="38" class="text-caption">{{ $t('quotations.dates_view.projects') }}</v-btn>
              <v-btn value="dates" height="38" class="text-caption">{{ $t('quotations.dates_view.title', { n: dates.length }) }}</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-checkbox v-model="onlyMine" :label="$t('quotations.filter.only_mine')" hide-details class="ma-0 pa-0" />
          </v-col>
        </v-row>

        <template v-if="!datesView">
          <!-- Yhteenvetolaatat tarjouksittain: klikkaus suodattaa listan vaiheeseen -->
          <v-slide-group v-if="$vuetify.breakpoint.xsOnly" class="pt-2 mx-n2" show-arrows="never">
            <v-slide-item v-for="tile in tiles" :key="tile.key">
              <v-card class="summary-tile mx-1" :class="{ 'summary-tile--active': tile.active }" elevation="0" v-on="tile.onClick ? { click: tile.onClick } : {}">
                <v-card-text class="py-2 px-3">
                  <div class="caption text-uppercase">{{ tile.label }}</div>
                  <div class="subtitle-1 font-weight-bold" :class="tile.active && 'primary--text'">{{ tile.value }}</div>
                  <div class="caption grey--text text--darken-1">{{ tile.sub }}</div>
                </v-card-text>
              </v-card>
            </v-slide-item>
          </v-slide-group>
          <v-row v-else class="pt-2" dense>
            <v-col v-for="tile in tiles" :key="tile.key" cols="6" md="auto" class="flex-grow-1">
              <v-card class="summary-tile" :class="{ 'summary-tile--active': tile.active }" v-on="tile.onClick ? { click: tile.onClick } : {}">
                <v-card-text class="py-2 px-3">
                  <div class="caption text-uppercase">{{ tile.label }}</div>
                  <div class="subtitle-1 font-weight-bold" :class="tile.active && 'primary--text'">{{ tile.value }}</div>
                  <div class="caption grey--text text--darken-1">{{ tile.sub }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Tarjousten suodattimet; haku osuu projektiin -->
          <v-row class="pt-2" dense align="center">
            <v-col cols="12" sm="4" lg="3" xl="2">
              <v-select
                v-model="filterStatus"
                :items="statusOptions"
                :placeholder="$t('quotations.filter.status')"
                item-text="desc"
                item-value="value"
                multiple
                hide-details
                single-line
                solo
                flat
                outlined
                dense
                prepend-inner-icon="filter_list"
                @blur="updateQuery({ status: filterStatus })"
              >
                <template v-slot:selection="{ index }">
                  <span v-if="index === 0">{{ filterStatusText }}</span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <v-select v-model="filterYear" :items="yearOptions" :label="$t('quotations.filter.year')" hide-details single-line solo flat outlined dense />
            </v-col>
            <v-col v-if="hasListFilters" cols="auto">
              <v-btn text small @click="resetListQuery">{{ $t('quotations.filter.clear') }}</v-btn>
            </v-col>
          </v-row>
        </template>
      </div>

      <v-col v-if="datesView" cols="12" class="pt-2">
        <quotation-dates-table :rows="dates" :loading="datesLoading" :busy="datesBusy" @open="id => open({ id })" @done="markDateDone" />
      </v-col>
      <v-col v-else cols="12" class="pt-2">
        <quotation-projects-table
          :projects="projects.content || []"
          :total-elements="projects.totalElements || 0"
          :loading="loading"
          :options="tableOptions"
          @update:options="onTableOptions"
          @open="open"
          @copy="copy"
        />
      </v-col>
    </v-col>
    <copy-quotation-dialog ref="copyDialog" @copied="onCopied" />
  </hb-basic-page>
</template>

<script>
import quotationApi, { STATUSES, ACTIVE_STATUSES } from '@/api/quotation';
import debounce from 'lodash.debounce';
import QuotationDatesTable from '@/components/Quotations/QuotationDatesTable';
import QuotationProjectsTable from '@/components/Quotations/QuotationProjectsTable';
import CopyQuotationDialog from '@/components/Quotations/CopyQuotationDialog';
import listQuery, { queryModels } from '@/helpers/listQuery';

const QUERY_FIELDS = {
  status: { type: 'list', default: () => [...ACTIVE_STATUSES], allowed: STATUSES, remember: true },
  year: { type: 'int', default: () => new Date().getFullYear(), nullable: true, remember: true },
  mine: { type: 'bool', default: false, remember: true },
  q: { type: 'string', default: '' },
  page: { type: 'int', default: 1 },
  size: { type: 'int', default: 50, allowed: [50, 100, 500, -1], remember: true },
  // name, dueDate (aikaisin avoin jättöpäivä) tai updatedAt
  sort: { type: 'string', default: 'updatedAt', remember: true },
  desc: { type: 'bool', default: true, remember: true },
  // 'dates' = avoimet määräpäivät projektilistan sijaan
  view: { type: 'string', default: '' },
};

export default {
  name: 'QuotationsView',
  components: { QuotationDatesTable, QuotationProjectsTable, CopyQuotationDialog },
  mixins: [listQuery({ key: 'quotations', fields: QUERY_FIELDS })],
  data() {
    return {
      loading: true,
      projects: {},
      summary: null,
      dates: [],
      datesLoading: false,
      datesBusy: false,
      // Vaihevalinnan luonnos: kirjataan URL:iin vasta valikon sulkeutuessa (blur)
      filterStatus: [],
      statusOptions: STATUSES.map(s => ({ desc: this.$t(`quotations.statuses.${s}`), value: s })),
    };
  },
  created() {
    this.debounceSearch = debounce(this.handleSearch, 400, { maxWait: 1000 });
  },
  mounted() {
    this.loadSummary();
  },
  computed: {
    ...queryModels({ filterYear: 'year', onlyMine: 'mine' }),
    datesView() {
      return this.listState.view === 'dates';
    },
    yearOptions() {
      const years = (this.summary && this.summary.years) || [new Date().getFullYear()];
      return [{ text: this.$t('quotations.filter.all_years'), value: null }, ...years.map(y => ({ text: String(y), value: Number(y) }))];
    },
    // Valinnan nimi kertoo mitä listassa näkyy: aktiiviset / kaikki / n vaihetta / yksi vaihe
    filterStatusText() {
      const sel = this.filterStatus || [];
      const same = (a, b) => a.length === b.length && a.every(x => b.includes(x));
      if (same(sel, ACTIVE_STATUSES)) return this.$t('quotations.filter.active');
      if (same(sel, STATUSES)) return this.$t('quotations.filter.all');
      if (sel.length === 1) return this.$t(`quotations.statuses.${sel[0]}`);
      return this.$t('quotations.filter.selected', { n: sel.length });
    },
    tiles() {
      const s = this.summary;
      const money = v => (v ? `${this.$formatNumber(v, '0,0')} €` : '0 €');
      const tile = status => {
        const d = s ? s.byStatus[status] : null;
        return {
          key: status,
          label: this.$t(`quotations.statuses.${status}`),
          value: d ? `${d.count} ${this.$t('quotations.summary.pcs')}` : '–',
          sub: d ? money(d.total) : '',
          active: this.filterStatus.length === 1 && this.filterStatus[0] === status,
          onClick: () => this.toggleStatusTile(status),
        };
      };
      const winRate = s && s.winRate != null ? `${Math.round(s.winRate * 100)} %` : this.$t('quotations.summary.no_data');
      return [
        tile('REQUEST'),
        tile('CALCULATION'),
        tile('REVIEW'),
        tile('READY'),
        tile('ORDERED'),
        {
          key: 'winrate',
          label: this.$t('quotations.summary.win_rate'),
          value: winRate,
          sub: s ? `${s.won} / ${s.won + s.lost}` : '',
          active: false,
          onClick: undefined,
        },
      ];
    },
  },
  methods: {
    handleSearch(searchPhrase) {
      this.updateQuery({ q: searchPhrase });
    },
    createNew() {
      this.$router.push({ name: 'tenant_quotation_new' });
    },
    open(offer) {
      this.$router.push({ name: 'tenant_quotation_edit', params: { id: offer.id } });
    },
    copy({ project, offer }) {
      this.$refs.copyDialog.open({ id: offer.id, name: project.name, code: offer.code, partyName: offer.partyName, deadline: offer.deadline });
    },
    onCopied(created) {
      this.$showSuccessNotification(this.$t('quotations.notifications.copied'));
      this.$router.push({ name: 'tenant_quotation_edit', params: { id: created.id } });
    },
    toggleStatusTile(status) {
      const active = this.filterStatus.length === 1 && this.filterStatus[0] === status;
      this.updateQuery({ status: active ? [...ACTIVE_STATUSES] : [status] });
    },
    async loadSummary() {
      try {
        this.summary = await quotationApi.getSummary(this.filterYear);
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async loadDates() {
      this.datesLoading = true;
      try {
        this.dates = await quotationApi.getUpcomingDates({ onlyMine: this.listState.mine });
      } catch (error) {
        this.$handleApiError(error);
      }
      this.datesLoading = false;
    },
    async markDateDone(item) {
      this.datesBusy = true;
      try {
        await quotationApi.markDateDone(item.offerId, item.dateIndex);
        this.$showSuccessNotification(this.$t('quotations.dates_view.marked_done'));
        await this.loadDates();
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.datesBusy = false;
    },
    async reload() {
      // Määräpäivät ladataan aina, koska lukumäärä näkyy näkymän valinnassa
      const dates = this.loadDates();
      if (this.datesView) return dates;
      this.loading = true;
      try {
        const s = this.listState;
        const filter = {};
        if (s.q) filter.search = s.q;
        if (s.status.length > 0) filter.status = s.status;
        if (s.year) filter.year = s.year;
        if (s.mine) filter.onlyMine = true;
        this.projects = await quotationApi.getQuotationsPaged(filter, this.tableOptions);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loading = false;
    },
  },
  watch: {
    'listState.status': {
      immediate: true,
      handler(status) {
        if (status.join() !== this.filterStatus.join()) this.filterStatus = [...status];
      },
    },
    filterYear() {
      this.loadSummary();
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-tile {
  min-width: 120px;
}
// Sama ulkoasu kuin työmaiden näkymän valinnassa (WorkSitesView .site-toggle)
.view-toggle ::v-deep {
  .v-btn__content {
    color: #000;
  }
  .v-btn--active {
    background-color: var(--v-primary-base) !important;
    .v-btn__content {
      color: #fff;
    }
  }
}
</style>
